import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ExternalLink, Globe } from "lucide-react";
import { useLanguage, useTranslation, LANGUAGES } from "@/i18n/translations";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_payment-links-hub-1/artifacts/p3qyw1lk_LOGO_TRANS.png";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const location = useLocation();
  const { lang, setLang } = useLanguage();
  const { t } = useTranslation();

  const navLinks = [
    { label: t("nav.home"), path: "/" },
    { label: t("nav.solutions"), path: "/solutions" },
    { label: "App", path: "/fonctionnalites" },
    { label: t("nav.pricing"), path: "/tarifs" },
    { label: t("nav.pos"), path: "/pos" },
    { label: t("nav.contact"), path: "/contact" },
    { label: t("nav.about"), path: "/a-propos" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setLangOpen(false);
  }, [location]);

  const currentLang = LANGUAGES.find(l => l.code === lang);

  return (
    <nav
      data-testid="navbar"
      className={`glass-nav fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? "scrolled" : ""}`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 flex items-center justify-between h-14 md:h-20">
        <Link to="/" className="flex items-center gap-2" data-testid="navbar-logo">
          <img src={LOGO_URL} alt="AyaPay" className="h-10 sm:h-12 md:h-14 w-auto object-contain" />
        </Link>

        {/* Desktop Nav */}
        <div className="hidden lg:flex items-center gap-7">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              data-testid={`nav-link-${link.path.replace("/", "") || "home"}`}
              className={`text-sm font-medium tracking-wide transition-colors duration-200 ${
                location.pathname === link.path
                  ? "text-[#1B2B5B]"
                  : "text-[#475569] hover:text-[#1B2B5B]"
              }`}
            >
              {link.label}
            </Link>
          ))}
        </div>

        <div className="hidden lg:flex items-center gap-3">
          {/* Language switcher */}
          <div className="relative">
            <button
              onClick={() => setLangOpen(!langOpen)}
              data-testid="language-switcher"
              className="flex items-center gap-1.5 text-sm font-medium text-[#475569] hover:text-[#1B2B5B] px-3 py-1.5 rounded-full border border-slate-200 transition-all"
            >
              <Globe size={14} />
              <span>{currentLang?.flag}</span>
            </button>
            {langOpen && (
              <div className="absolute top-full right-0 mt-2 bg-white rounded-xl border border-slate-200 shadow-lg py-2 min-w-[140px] z-50">
                {LANGUAGES.map((l) => (
                  <button
                    key={l.code}
                    onClick={() => { setLang(l.code); setLangOpen(false); }}
                    data-testid={`lang-${l.code}`}
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-slate-50 transition-colors ${
                      lang === l.code ? "text-[#00C853] font-medium" : "text-[#475569]"
                    }`}
                  >
                    <span className="mr-2">{l.flag}</span> {l.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          <a
            href="https://pay.ayapay.ch"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-dashboard-link"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-[#1B2B5B] border border-[#1B2B5B] rounded-full px-5 py-2 hover:bg-[#1B2B5B] hover:text-white transition-all duration-200"
          >
            {t("nav.client")}
            <ExternalLink size={14} />
          </a>
          <Link
            to="/contact"
            data-testid="nav-cta"
            className="inline-flex items-center text-sm font-semibold bg-[#00C853] text-white rounded-full px-5 py-2 hover:bg-[#00A844] transition-all duration-200"
          >
            {t("nav.demo")}
          </Link>
        </div>

        {/* Mobile toggle */}
        <button
          data-testid="mobile-menu-toggle"
          className="lg:hidden p-2 text-[#1B2B5B]"
          onClick={() => setMobileOpen(!mobileOpen)}
        >
          {mobileOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div data-testid="mobile-menu" className="lg:hidden bg-white border-t border-slate-100 px-6 pb-6 pt-4 space-y-3">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`block py-2 text-sm font-medium ${
                location.pathname === link.path ? "text-[#1B2B5B]" : "text-[#475569]"
              }`}
            >
              {link.label}
            </Link>
          ))}
          <a
            href="https://pay.ayapay.ch"
            target="_blank"
            rel="noopener noreferrer"
            className="block py-2 text-sm font-medium text-[#1B2B5B]"
          >
            {t("nav.client")}
          </a>
          {/* Mobile language */}
          <div className="flex flex-wrap gap-2 py-2">
            {LANGUAGES.map((l) => (
              <button
                key={l.code}
                onClick={() => setLang(l.code)}
                className={`text-xs px-3 py-1.5 rounded-full border ${
                  lang === l.code ? "border-[#00C853] text-[#00C853] bg-[#00C853]/5" : "border-slate-200 text-[#475569]"
                }`}
              >
                {l.flag}
              </button>
            ))}
          </div>
          <Link
            to="/contact"
            className="block text-center text-sm font-semibold bg-[#00C853] text-white rounded-full px-5 py-2.5 mt-2"
          >
            {t("nav.demo")}
          </Link>
        </div>
      )}
    </nav>
  );
}
